/**
 * Swotify backend auth for React (main swotify-v2 app).
 * Send X-Swotify-Client: react for multi-role selection flow.
 *
 * Access token: short-lived Bearer in localStorage (~30 min).
 * Refresh token: HttpOnly cookie (preferred) + localStorage fallback.
 *   localStorage is required for "remember me" overnight / multi-day PWA reopen —
 *   sessionStorage is cleared when the tab/PWA closes, which caused refresh 401
 *   after hours of inactivity even though the server window is 30 days.
 */

import { getApiBase } from './apiBase';
import { queryClient } from './queryClient';
import { swotifyFetch } from './swotifyFetch';

const API_BASE = getApiBase();

/** Include cookies (refresh) on same-origin proxy and cross-origin API hosts. */
export const AUTH_FETCH_CREDENTIALS = 'include';

/**
 * Durable refresh fallback (survives PWA/browser close).
 * Cleared on logout / refresh failure.
 */
const REFRESH_FALLBACK_KEY = 'swotify_refresh_token_fallback';

/** Refresh access token this many ms before JWT exp. */
const PROACTIVE_REFRESH_SKEW_MS = 90_000;

let refreshInFlight = null;
let proactiveTimer = null;

function isRecord(v) {
  return typeof v === 'object' && v !== null;
}

function readRefreshFallback() {
  try {
    const fromLocal = localStorage.getItem(REFRESH_FALLBACK_KEY);
    if (fromLocal) return fromLocal;
    // One-time migrate from older sessionStorage fallback (pre remember-me fix).
    const fromSession = sessionStorage.getItem(REFRESH_FALLBACK_KEY);
    if (fromSession) {
      localStorage.setItem(REFRESH_FALLBACK_KEY, fromSession);
      sessionStorage.removeItem(REFRESH_FALLBACK_KEY);
      return fromSession;
    }
    return '';
  } catch {
    return '';
  }
}

function writeRefreshFallback(token) {
  try {
    if (token) {
      localStorage.setItem(REFRESH_FALLBACK_KEY, token);
      sessionStorage.removeItem(REFRESH_FALLBACK_KEY);
    } else {
      localStorage.removeItem(REFRESH_FALLBACK_KEY);
      sessionStorage.removeItem(REFRESH_FALLBACK_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Decode JWT payload without verifying (client scheduling only). */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function scheduleProactiveRefresh(accessToken) {
  if (typeof window === 'undefined') return;
  if (proactiveTimer) {
    window.clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
  const payload = decodeJwtPayload(accessToken);
  const expSec = Number(payload?.exp);
  if (!expSec) return;
  const msUntilRefresh = expSec * 1000 - Date.now() - PROACTIVE_REFRESH_SKEW_MS;
  const delay = Math.max(5_000, Math.min(msUntilRefresh, 24 * 60 * 60 * 1000));
  proactiveTimer = window.setTimeout(() => {
    refreshAccessToken().catch(() => {
      /* interceptor / ensureSession will surface expiry */
    });
  }, delay);
}

/** Remove secrets from `/auth/me` (or any user JSON) before persisting or using in UI. */
export function sanitizeUserForClient(me) {
  if (me == null) return null;
  if (!isRecord(me)) return me;
  const rest = { ...me };
  delete rest.password_hash;
  delete rest.password;
  return rest;
}

/** Normalize one school row from GET /auth/school-codes (handles numeric codes & alternate keys). */
function normalizeSchoolLoginRow(x) {
  if (!isRecord(x)) return null;
  const rawCode = x.code ?? x.school_code ?? x.schoolCode;
  if (rawCode == null || rawCode === '') return null;
  const code = String(rawCode).trim();
  if (!code) return null;
  const rawName = x.name ?? x.school_name ?? x.label;
  let name = '';
  if (typeof rawName === 'string') name = rawName.trim();
  else if (rawName != null) name = String(rawName).trim();
  return { code, name: name || code };
}

export async function fetchSchoolsForLogin() {
  let res;
  try {
    res = await fetch(`${API_BASE}/auth/school-codes`, {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return [];
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    return [];
  }
  let list = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (isRecord(data)) {
    const inner = data.schools ?? data.items ?? data.data ?? data.results;
    if (Array.isArray(inner)) list = inner;
  }
  const out = [];
  for (const x of list) {
    const row = normalizeSchoolLoginRow(x);
    if (row) out.push(row);
  }
  return out;
}

export function isPendingRoleSelection(data) {
  if (!isRecord(data)) return false;
  return data.requires_role_selection === true && typeof data.selection_token === 'string';
}

export async function loginReact(email, password, schoolCode) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    credentials: AUTH_FETCH_CREDENTIALS,
    headers: {
      'Content-Type': 'application/json',
      'X-Swotify-Client': 'react',
    },
    body: JSON.stringify({
      email,
      password,
      school_code: schoolCode,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      isRecord(data) && typeof data.detail === 'string'
        ? data.detail
        : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return data;
}

export async function fetchCurrentUser(accessToken) {
  const token =
    accessToken ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('swotify_access_token')
      : null);
  if (!token) {
    throw new Error('Missing access token');
  }

  const schoolCode = typeof localStorage !== 'undefined' ? localStorage.getItem('swotify_school_code') : null;

  const res = await fetch(`${API_BASE}/auth/me`, {
    credentials: AUTH_FETCH_CREDENTIALS,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(schoolCode ? { 'X-School-Code': schoolCode } : {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      isRecord(data) && typeof data.detail === 'string'
        ? data.detail
        : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return sanitizeUserForClient(data);
}

/**
 * GET /auth/me with current (or provided) token, then persist sanitized user for layout/sidebar.
 * Call after login, on profile screens, or when refreshing session context.
 */
export async function refreshCurrentUser(accessToken) {
  const me = await fetchCurrentUser(accessToken);
  persistCurrentUser(me);
  return me;
}

/**
 * Rotate refresh token (HttpOnly cookie and/or Bearer fallback) → new access token.
 * Safe to call concurrently (single-flight).
 */
export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const fallback = readRefreshFallback();
    const headers = { Accept: 'application/json' };
    // Prefer Bearer fallback when present so cross-origin HTTP still works.
    // Cookie is still sent via credentials:include when the browser allows it.
    if (fallback) {
      headers.Authorization = `Bearer ${fallback}`;
    }
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: AUTH_FETCH_CREDENTIALS,
      headers,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail =
        isRecord(data) && typeof data.detail === 'string'
          ? data.detail
          : `HTTP ${res.status}`;
      throw new Error(detail);
    }
    if (!data?.access_token) {
      throw new Error('Refresh did not return an access token');
    }
    persistSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token || undefined,
      role: data.role,
      context_id: data.context_id,
      school_code:
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('swotify_school_code') || undefined
          : undefined,
    });
    return data.access_token;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * If access token is missing/expired but refresh cookie is still valid,
 * restore a session (PWA reopen / day-to-day return).
 * After 30 days of no use the refresh cookie/session expires → clear local
 * state so the user must sign in again.
 */
export async function ensureSession() {
  const existing =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('swotify_access_token')
      : null;
  if (existing) {
    try {
      await fetchCurrentUser(existing);
      scheduleProactiveRefresh(existing);
      return existing;
    } catch {
      /* try refresh below */
    }
  }
  try {
    return await refreshAccessToken();
  } catch {
    clearSession();
    return null;
  }
}

export async function fetchMyAssignedClasses(accessToken) {
  const token =
    accessToken ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('swotify_access_token')
      : null);
  if (!token) {
    throw new Error('Missing access token');
  }

  const schoolCode = typeof localStorage !== 'undefined' ? localStorage.getItem('swotify_school_code') : null;

  const res = await fetch(`${API_BASE}/users/me/assigned-classes`, {
    credentials: AUTH_FETCH_CREDENTIALS,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(schoolCode ? { 'X-School-Code': schoolCode } : {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      isRecord(data) && typeof data.detail === 'string'
        ? data.detail
        : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return Array.isArray(data?.assigned_classes) ? data.assigned_classes : [];
}

export async function fetchAllClasses(accessToken) {
  const token =
    accessToken ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('swotify_access_token')
      : null);
  if (!token) {
    throw new Error('Missing access token');
  }

  const schoolCode =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('swotify_school_code') ||
        localStorage.getItem('swotify_active_school_code')
      : null;

  const res = await fetch(`${API_BASE}/students/classes`, {
    credentials: AUTH_FETCH_CREDENTIALS,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(schoolCode ? { 'X-School-Code': schoolCode } : {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      isRecord(data) && typeof data.detail === 'string'
        ? data.detail
        : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
}

export async function selectRole(selectionToken, role, contextId) {
  const body = { role, context_id: contextId || null };
  const res = await fetch(`${API_BASE}/auth/select-role`, {
    method: 'POST',
    credentials: AUTH_FETCH_CREDENTIALS,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${selectionToken}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      isRecord(data) && typeof data.detail === 'string'
        ? data.detail
        : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return data;
}

export async function fetchMyRoles() {
  return swotifyFetch('/auth/my-roles', { silent: true });
}

export async function switchRole(role, contextId) {
  return swotifyFetch('/auth/switch-role', {
    method: 'POST',
    body: JSON.stringify({ role, context_id: contextId || null }),
    silent: true,
  });
}

/** Drop cached queries and role-specific client picks after switching roles. */
export function clearRoleScopedClientState() {
  try {
    queryClient.clear();
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('swotify_portal_selected_student');
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('swotify_active_grade_level');
      localStorage.removeItem('swotify_active_section');
      localStorage.removeItem('swotify_teacher_logs');
    }
  } catch {
    /* ignore */
  }
}

/**
 * Persist new tokens, reset role-scoped caches, refresh `/auth/me`, navigate.
 * @param {Record<string, unknown>} tokens
 * @param {(path: string, opts?: { replace?: boolean }) => void} navigate
 */
export async function applyRoleSwitch(tokens, navigate) {
  persistSession(tokens);
  clearRoleScopedClientState();
  await refreshCurrentUser(tokens.access_token);
  navigate(dashboardPathForRole(tokens.role), { replace: true });
}

export function persistSession(tokens) {
  try {
    if (tokens?.access_token) {
      localStorage.setItem('swotify_access_token', tokens.access_token);
      scheduleProactiveRefresh(tokens.access_token);
    }
    // Prefer HttpOnly cookie; also keep body refresh in localStorage so
    // closing the PWA overnight still refreshes until the 30-day idle limit.
    if (tokens?.refresh_token) {
      writeRefreshFallback(tokens.refresh_token);
    }
    localStorage.removeItem('swotify_refresh_token');
    if (tokens?.role) localStorage.setItem('swotify_role', tokens.role);
    if (tokens?.school_code) {
      localStorage.setItem('swotify_school_code', tokens.school_code);
    }
    if (tokens?.schema_name) {
      localStorage.setItem('swotify_schema_name', String(tokens.schema_name).trim());
    }
    if (tokens?.subdomain) {
      localStorage.setItem('swotify_school_subdomain', String(tokens.subdomain).trim().toLowerCase());
    }
    if (tokens?.context_id) {
      localStorage.setItem('swotify_context_id', tokens.context_id);
    } else if (tokens && Object.prototype.hasOwnProperty.call(tokens, 'context_id')) {
      localStorage.removeItem('swotify_context_id');
    }
  } catch {
    /* ignore */
  }
}

export function persistCurrentUser(user) {
  try {
    const safe = user == null ? {} : sanitizeUserForClient(user) || {};
    localStorage.setItem('swotify_current_user', JSON.stringify(safe));
  } catch {
    /* ignore */
  }
}

export function getPersistedCurrentUser() {
  try {
    const raw = localStorage.getItem('swotify_current_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    return sanitizeUserForClient(parsed);
  } catch {
    return null;
  }
}

export function clearSession() {
  try {
    if (typeof window !== 'undefined') {
      window.__swotifySuppressSessionModalUntil = Date.now() + 4000;
      if (proactiveTimer) {
        window.clearTimeout(proactiveTimer);
        proactiveTimer = null;
      }
    }
    localStorage.removeItem('swotify_access_token');
    localStorage.removeItem('swotify_refresh_token');
    localStorage.removeItem('swotify_role');
    localStorage.removeItem('swotify_context_id');
    localStorage.removeItem('swotify_current_user');
    localStorage.removeItem('swotify_school_code');
    localStorage.removeItem('swotify_schema_name');
    localStorage.removeItem('swotify_school_subdomain');
    writeRefreshFallback('');
  } catch {
    /* ignore */
  }
}

/** Server logout + clear local session (revokes refresh cookie / DB sessions). */
export async function logoutReact() {
  try {
    const { revokeStoredPushToken } = await import('./pushNotifications');
    await revokeStoredPushToken();
  } catch {
    /* still log out */
  }
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('swotify_access_token')
      : null;
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: AUTH_FETCH_CREDENTIALS,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    /* still clear locally */
  }
  clearSession();
}

/** Map API role to dashboard path (HashRouter paths, no hash prefix). */
export function dashboardPathForRole(role) {
  switch (role) {
    case 'super_user':
      return '/super-admin-dashboard';
    case 'school_admin':
      return '/admin-dashboard';
    case 'teacher':
    case 'counselor':
      return '/teacher-dashboard';
    case 'parent':
    case 'student':
      return '/student-dashboard/dashboard';
    default:
      return '/student-dashboard/dashboard';
  }
}
